import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  NgModule,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute } from '@angular/router';
import { SearchEngineFieldJobsEuresInput } from '@domains/search-engine/entities/eures-jobs-input-search-field';
import { EuresAlgoliaFilterItem } from '@models/eures/search-filters';
import { EuresSearchFiltersWebservice } from '@webservices/eures-algolia-search/search-filters.webservice';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map, shareReplay, switchMap, take } from 'rxjs/operators';

@Component({
  selector: 'app-jobs-eures-input-search',
  templateUrl: './jobs-eures-input-search.component.html',
  styleUrls: ['./jobs-eures-input-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JobsEuresInputSearchComponent),
      multi: true,
    },
  ],
})
export class JobsEuresInputSearchComponent implements OnInit, ControlValueAccessor {
  private _value?: string;
  @Input() field!: SearchEngineFieldJobsEuresInput;
  @Input() modalOpen!: boolean;
  @Input() liveSearch!: boolean;

  @Output() hasChanged = new EventEmitter<boolean>();

  isFocused = false;
  euresJobs$!: Observable<EuresAlgoliaFilterItem[]>;

  euresJobsForm: FormGroup;

  get euresJobsControl(): FormControl {
    return this.euresJobsForm.get('metier') as FormControl;
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly fb: FormBuilder,
    private readonly euresSearchFilterService: EuresSearchFiltersWebservice
  ) {
    this.euresJobsForm = this.fb.group({
      metier: '',
    });
  }

  set value(val: string) {
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  get value(): string {
    return this._value ?? '';
  }

  onChange: (val: string) => void = () => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  clearValue(event: any): void {
    this.euresJobsControl.setValue(undefined);
    this.value = '';

    if (this.liveSearch) {
      this.hasChanged.emit();
    }

    if (event) {
      event.stopPropagation();
    }
  }

  writeValue(val: string): void {
    this.value = val;
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = (val) => {
      fn(val);
      this.cdr.markForCheck();
    };
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(
        take(1),
        map((params) => params.get('metier')),
        switchMap((jobIdParam) =>
          this.euresSearchFilterService.getOccupations('', { facetFilters: [`objectID:${jobIdParam}`] })
        ),
        map((response) => response.hits[0])
      )
      .subscribe({
        next: (job) => {
          this.euresJobsControl.setValue(job);
        },
      });

    this.euresJobs$ = this.euresJobsControl.valueChanges.pipe(
      debounceTime(300),
      filter((q) => q && q.length > 0),
      distinctUntilChanged(),
      switchMap((q) => this.euresSearchFilterService.getOccupations(q)),
      map((response) => response.hits),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  trackByJobName(_: number, job: EuresAlgoliaFilterItem): string {
    return job.name;
  }

  onClose(): void {
    if (this.value) {
      return;
    }

    this.euresJobs$
      .pipe(
        first(),
        map((jobs) => jobs[0]),
        filter((job) => !!job)
      )
      .subscribe({
        next: (job) => {
          this.value = job.objectID;
          this.euresJobsControl.setValue(job);
        },
      });
  }

  selectValue(event: MatAutocompleteSelectedEvent): void {
    this.value = (event.option.value as EuresAlgoliaFilterItem).objectID;
    if (this.liveSearch) {
      this.hasChanged.emit();
    }
  }

  displayEuresJobName(euresJob?: EuresAlgoliaFilterItem): string {
    return euresJob?.name || '';
  }
}

@NgModule({
  imports: [
    CommonModule,
    MatIconModule,
    MatOptionModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
  ],
  declarations: [JobsEuresInputSearchComponent],
  exports: [JobsEuresInputSearchComponent],
})
export class SearchEngineJobsEuresInputSearchModule {}
